import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import {
  Button,
  Drawer,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

import axios from 'axios'
import DateRangePicker from '../../../components/DateRangePicker'
import SelectSearch from '../../../components/SelectSearch'
import CategorySelectSearch from '../../../components/SelectSearch/CategorySelectSearch'
import ChannelSelectSearch from '../../../components/SelectSearch/ChannelSelectSearch'
import ProductGroupSelectSearch from '../../../components/SelectSearch/ProductGroupSelectSearch'
import ProductSelectSearch from '../../../components/SelectSearch/ProductSelectSearch'
import UserSelectSearch from '../../../components/SelectSearch/UserSelectSearch'
import { IOptionSelect } from '../../../components/SelectSearchMultiple'
import { ImageLinks, LogoTypeProvider } from '../../../constants/image-link'
import useAxios from '../../../hooks/useAxios'
import { useUser } from '../../../hooks/useUser'
import { ICategory } from '../../../types/category'
import { IDistributionChannel } from '../../../types/distribution-channel'
import {
  EOrderDateType,
  EOrderPrintedStatus,
  EOrderStatus,
  EOrderType,
  OrderShippingProvider,
} from '../../../types/order'
import { PaginationResponseInterface } from '../../../types/pagination'
import { IProduct } from '../../../types/product'
import { IUserLists } from '../../../types/user'
import { AllowShippingProviderLists } from '../../../utils/provider'
import { validateMinMaxValue } from '../../../utils/validateMinMaxValue'
import { IFilterMapped } from './TrackingFilter'
import { DATE_TYPES } from '../../../constants/global'

export enum EOrderOverdue {
  TRUE = 'true',
  FALSE = 'false',
  NULL = 'null',
}

export enum EStatusTypeOrderCrmTelesale {
  ORDER = 'order',
  CRM = 'crm',
  TELESALE = 'telesale',
  UPSALE = 'upsale',
}

export enum EStatusOrderShowTracking {
  TRACKING = 'tracking',
  NO_TRACKING = 'no_tracking',
  PENDING = 'pending',
  BANK = 'BANK',
  COD = 'COD',
}

interface OrderFilterDrawerProps {
  isOpen: boolean
  onClose: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
}

const menuItemsTypeDate = [
  {
    label: 'วันที่คำสั่งซื้อ',
    value: EOrderDateType.ORDERDATE,
  },
  {
    label: 'วันที่สร้างคำสั่งซื้อ',
    value: EOrderDateType.CREATED,
  },
  {
    label: 'วันที่ขนส่งรับสินค้า',
    value: EOrderDateType.PICKUP,
  },
  {
    label: 'วันที่พิมพ์',
    value: EOrderDateType.PRINTED,
  },
]

const menuItemsTypeOrderType = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'เก็บเงินปลายทาง',
    value: EOrderType.COD,
  },
  {
    label: 'โอนเงินผ่านธนาคาร',
    value: EOrderType.BANK,
  },
]

const menuItemsTypeOrderStatusCRM = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'รายการคำสั่งซื้อ',
    value: EStatusTypeOrderCrmTelesale.ORDER,
  },
  {
    label: 'รายการคำสั่งซื้อ CRM',
    value: EStatusTypeOrderCrmTelesale.CRM,
  },
  {
    label: 'รายการคำสั่งซื้อ Telesale',
    value: EStatusTypeOrderCrmTelesale.TELESALE,
  },
  {
    label: 'รายการคำสั่งซื้อ Upsale',
    value: EStatusTypeOrderCrmTelesale.UPSALE,
  },
]

const menuItemsTypeOrderStatus = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'รอดำเนินการ',
    value: EOrderStatus.PENDING,
  },
  {
    label: 'รอดำเนินการจัดส่ง',
    value: EOrderStatus.TRACKING,
  },
  {
    label: 'ยกเลิก',
    value: EOrderStatus.CANCLELLED,
  },
  {
    label: 'ตีกลับ',
    value: EOrderStatus.RETURN,
  },
  {
    label: 'ตีกลับ (รับสินค้าแล้ว)',
    value: EOrderStatus.RETURNED,
  },
  {
    label: 'ระหว่างขนส่ง',
    value: EOrderStatus.TRANSIT,
  },
  {
    label: 'ส่งสำเร็จ',
    value: EOrderStatus.DELIVERED,
  },
  {
    label: 'สำเร็จ',
    value: EOrderStatus.SUCCESS,
  },
]

const menuItemsTypeOrderPrintedStatus = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'พิมพ์แล้ว',
    value: EOrderPrintedStatus.PRINTED,
  },
  {
    label: 'ยังไม่ได้พิมพ์',
    value: EOrderPrintedStatus.UNPRINTED,
  },
]

const menuItemsTypeOrderOverdueStatus = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'ค้างชำระ',
    value: EOrderOverdue.TRUE,
  },
  {
    label: 'pickup',
    value: EOrderOverdue.FALSE,
  },
]

const menuItemsTypeStatus = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'เฉพาะรายการที่มี Tracking',
    value: EStatusOrderShowTracking.TRACKING,
  },
  {
    label: 'ยังไม่มี Tracking',
    value: EStatusOrderShowTracking.NO_TRACKING,
  },
  {
    label: 'สินค้าค้างส่ง',
    value: EStatusOrderShowTracking.PENDING,
  },
  {
    label: 'เฉพาะโอนเงิน',
    value: EStatusOrderShowTracking.BANK,
  },
  {
    label: 'เฉพาะ COD',
    value: EStatusOrderShowTracking.COD,
  },
]

const menuItemsTypePaymentMethod = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'โอนเงินผ่านธนาคาร',
    value: EOrderType.BANK,
  },
  {
    label: 'เก็บเงินปลายทาง',
    value: EOrderType.COD,
  },
]

interface ICity {
  id: number
  name_th: string
  name_en: string
  geography_id: number
  created_at: string
  updated_at: string
  deleted_at: string | null
}

const menuItemsProviderType = [
  {
    label: 'เลือกทั้งหมด',
    value: 'all',
  },
  {
    label: 'Kerry',
    value: OrderShippingProvider.KERRY,
  },
  {
    label: 'Flash',
    value: OrderShippingProvider.FLASH,
  },
  {
    label: 'J&T',
    value: OrderShippingProvider.JNT,
  },
  {
    label: 'Thai Post',
    value: OrderShippingProvider.THAIPOST,
  },
  {
    label: 'Best Express',
    value: OrderShippingProvider.BESTEXPRESS,
  },
  {
    label: 'SPX Express',
    value: OrderShippingProvider.SPXEXPRESS,
  },
]

const TrackingFilterDrawer = ({
  isOpen,
  onClose,
  onFilterChange,
  filter,
  onSearch,
}: OrderFilterDrawerProps) => {
  const {
    page,
    perPage,
    search,
    startDate,
    endDate,
    status,
    categories,
    products,
    productGroups,
    distributionChannels,
    minTotal,
    maxTotal,
    statusShowCrmTelesale,
    dateType,
    paymentMethod,
    province,
    telesale,
    overdue,
    statusOrderShowTracking,
    isPrinted,
  } = filter
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { merchant } = useUser()
  const [provinceList, setProvinceList] = useState<ICity[]>([])
  const [searchProduct, setSearchProduct] = useState<string>('')
  const [searchCategory, setSearchCategory] = useState<string>('')
  const [searchProvince, setSearchProvince] = useState<string>('')
  const [searchActor, setSearchActor] = useState<string>('')
  const [searchComission, setSearchComission] = useState<string>('')
  const [searchDistributionChannel, setSearchDistributionChannel] =
    useState<string>('')
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')

  const handleFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  const handleReset = useCallback(() => {
    onFilterChange({
      page: 1,
      perPage: 15,
      status: 'pending',
      dateType: EOrderDateType.CREATED,
      search: undefined,
      startDate: undefined,
      endDate: undefined,
      categories: undefined,
      products: undefined,
      productGroups: undefined,
      distributionChannels: undefined,
      minTotal: undefined,
      maxTotal: undefined,
      quantity: undefined,
      statusShowCrmTelesale: undefined,
      paymentMethod: undefined,
      province: undefined,
      telesale: undefined,
      overdue: undefined,
      statusOrderShowTracking: undefined,
      trackingProvider: undefined,
    })
    onSearch({
      page: 1,
      perPage: 15,
      status: 'pending',
      dateType: EOrderDateType.CREATED,
      search: undefined,
      startDate: undefined,
      endDate: undefined,
      categories: undefined,
      quantity: undefined,
      products: undefined,
      productGroups: undefined,
      distributionChannels: undefined,
      minTotal: undefined,
      maxTotal: undefined,
      statusShowCrmTelesale: undefined,
      paymentMethod: undefined,
      province: undefined,
      telesale: undefined,
      overdue: undefined,
      statusOrderShowTracking: undefined,
      trackingProvider: undefined,
    })
    setResetAutoComplete(new Date().getTime().toString())
  }, [page, perPage, onFilterChange, onSearch])

  const productsList = useAxios<PaginationResponseInterface<IProduct>>(
    {
      method: 'GET',
      url: '/product',
      params: {
        page: 1,
        perPage: 5,
        search: searchProduct,
        startDate: '',
        endDate: '',
      },
    },
    {
      revalidateOnFocus: false,
    },
  )

  const handleSearchProduct = async (value: string) => {
    setSearchProduct(value)
    await productsList.mutate()
  }

  const actorsList = useAxios<PaginationResponseInterface<IUserLists>>(
    {
      method: 'GET',
      url: '/users/merchant',
      params: {
        page: 1,
        perPage: 5,
        search: searchActor,
        startDate: '',
        endDate: '',
      },
    },
    {
      revalidateOnFocus: false,
    },
  )

  const handleSearchActor = async (value: string) => {
    setSearchActor(value)
    try {
      await actorsList.mutate()
    } catch (error) {}
  }

  const comissionsList = useAxios<PaginationResponseInterface<IUserLists>>(
    {
      method: 'GET',
      url: '/users/merchant',
      params: {
        page: 1,
        perPage: 5,
        search: searchComission,
        startDate: '',
        endDate: '',
      },
    },
    {
      revalidateOnFocus: false,
    },
  )

  const handleSearchComission = async (value: string) => {
    setSearchComission(value)
    try {
      await comissionsList.mutate()
    } catch (error) {}
  }

  const categoriesList = useAxios<PaginationResponseInterface<ICategory>>(
    {
      method: 'GET',
      url: '/category',
      params: {
        page: 1,
        perPage: 5,
        search: searchCategory,
        startDate: '',
        endDate: '',
      },
    },
    {
      revalidateOnFocus: false,
    },
  )

  const handleSearchCategory = async (value: string) => {
    setSearchCategory(value)
    await categoriesList.mutate()
  }

  const distributionChannelsList = useAxios<
    PaginationResponseInterface<IDistributionChannel>
  >(
    {
      method: 'GET',
      url: '/distribution-channels',
      params: {
        page: 1,
        perPage: 5,
        search: searchDistributionChannel,
        startDate: '',
        endDate: '',
      },
    },
    {
      revalidateOnFocus: false,
    },
  )

  const handleSearchDistributionChannel = async (value: string) => {
    setSearchDistributionChannel(value)
    try {
      await distributionChannelsList.mutate()
    } catch (error) {}
  }

  useEffect(() => {
    const fetchProvince = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_FORMAT_PROVINCE_URL}`,
        )
        setProvinceList(response.data)
      } catch (error) {
        console.error('Error fetching province', error)
      }
    }
    fetchProvince()
  }, [])

  return (
    <Drawer
      anchor={'right'}
      keepMounted
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        style: {
          width: isMobile ? '80%' : '40%',
          maxWidth: isMobile ? '80%' : '40%',
        },
      }}
    >
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item xs={12} lg={4}>
          <TextField
            label={'ค้นหาจาก'}
            fullWidth
            id="searchType"
            name="searchType"
            variant="filled"
            value={filter.searchType || 'customer_name'}
            onChange={(e) => handleFilterChange({ searchType: e.target.value })}
            select
          >
            <MenuItem value="order_id">รหัสคำสั่งซื้อ</MenuItem>
            <MenuItem value="order_code">เลขออเดอร์ </MenuItem>
            <MenuItem value="customer_name">ชื่อลูกค้า</MenuItem>
            <MenuItem value="customer_phone">หมายเลขโทรศัพท์</MenuItem>
            <MenuItem value="tracking_number">หมายเลขพัสดุ</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} lg={8}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            label={'ค้นหาจากรหัสคำสั่งซื้อ, ชื่อลูกค้า, หมายเลขโทรศัพท์'}
            fullWidth
            id="search"
            name="search"
            autoFocus
            variant="filled"
            value={search || ''}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          <TextField
            label={'ค้นหาจากหมายเลขพัสดุ (ค้นหาได้จากเลขพัสดุ เก่าและใหม่)'}
            fullWidth
            id="trackingNumber"
            name="trackingNumber"
            autoFocus
            variant="filled"
            value={filter.trackingNumber || ''}
            onChange={(e) =>
              handleFilterChange({ trackingNumber: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="trackingProvider-label">ช่องทางการขนส่ง</InputLabel>
            <Select
              key={resetAutoComplete}
              id="trackingProvider"
              name="trackingProvider"
              labelId="trackingProvider-label"
              onChange={(e) =>
                onFilterChange({
                  ...filter,
                  trackingProvider: e.target.value as OrderShippingProvider,
                })
              }
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>ช่องทางการขนส่ง</em>
              </MenuItem>
              <MenuItem value="all" key="all">
                ไม่เลือก
              </MenuItem>
              {AllowShippingProviderLists(merchant).map(
                (item, index: number) => (
                  <MenuItem value={item.value} key={index}>
                    {item.value !== 'all' && (
                      <img
                        style={{ marginRight: 5 }}
                        src={
                          ImageLinks[
                            `LOGO_${item.value.toUpperCase()}` as LogoTypeProvider
                          ]
                        }
                        alt="smallLogoSocial"
                        height={20}
                      />
                    )}
                    {item.label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={12}>
          {/* <SelectSearchMultiple<IProduct>
            resetAutoComplete={resetAutoComplete}
            placeholder={'เลือกสินค้า'}
            label={'เลือกสินค้า'}
            onSelect={(e) => {
              const selectedProductIds = e?.map(
                (product) => product?.value?.name,
              )
              handleFilterChange({ products: selectedProductIds })
            }}
            search={searchProduct}
            onSearch={handleSearchProduct}
            options={
              productsList?.data?.records
                .filter(
                  (product: IProduct) => !products?.includes(product.name),
                )
                .map(
                  (product: IProduct) =>
                    product.isAvailable && {
                      label: `${product.name} (${product.code})`,
                      image: product.image,
                      value: product,
                    },
                )
                .reduce((acc: IOptionSelect<IProduct>[], product) => {
                  if (product) {
                    acc.push(product)
                  }
                  return acc
                }, []) || ([] as IOptionSelect<IProduct>[])
            }
          /> */}
          <ProductSelectSearch
            resetAutoComplete={resetAutoComplete}
            products={products}
            onSelect={(e) => {
              const selectedProductIds = e?.map(
                (product: any) => product?.value?.objectId,
              )
              handleFilterChange({ products: selectedProductIds })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          <ProductGroupSelectSearch
            resetAutoComplete={resetAutoComplete}
            productGroups={productGroups}
            onSelect={(e) => {
              const selectedGroupIds = e?.map((group) => group.value.objectId)
              handleFilterChange({ productGroups: selectedGroupIds })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          {/* <SelectSearchMultiple<ICategory>
            resetAutoComplete={resetAutoComplete}
            placeholder={'เลือกหมวดหมู่สินค้า'}
            label={'เลือกหมวดหมู่สินค้า'}
            onSelect={(e) => {
              const selectedCategoryIds = e?.map(
                (category) => category?.value?.name,
              )
              onFilterChange({ ...filter, categories: selectedCategoryIds })
            }}
            search={searchCategory}
            onSearch={handleSearchCategory}
            options={
              categoriesList.data?.records
                .filter(
                  (category: ICategory) => !categories?.includes(category.name),
                )
                .map(
                  (category: ICategory) =>
                    category.isAvailable && {
                      label: category.name,
                      value: category,
                    },
                )
                .reduce((acc: IOptionSelect<ICategory>[], category) => {
                  if (category) {
                    acc.push(category)
                  }
                  return acc
                }, []) || ([] as IOptionSelect<ICategory>[])
            }
          /> */}
          <CategorySelectSearch
            resetAutoComplete={resetAutoComplete}
            categories={categories}
            onSelect={(e) => {
              const selectedCategoryIds = e?.map(
                (category: any) => category?.value?.objectId,
              )
              onFilterChange({ ...filter, categories: selectedCategoryIds })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          {/* <SelectSearchMultiple<IDistributionChannel>
            resetAutoComplete={resetAutoComplete}
            placeholder={'เลือกช่องทางการขาย'}
            label={'เลือกช่องทางการขาย'}
            onSelect={(e) => {
              const selectedChannelIds = e?.map(
                (channel) => channel?.value?.objectId,
              )
              onFilterChange({
                ...filter,
                distributionChannels: selectedChannelIds,
              })
            }}
            search={searchDistributionChannel}
            onSearch={handleSearchDistributionChannel}
            options={
              distributionChannelsList.data?.records
                .filter(
                  (distributionChannel: IDistributionChannel) =>
                    !distributionChannels?.includes(
                      distributionChannel.objectId,
                    ),
                )
                .map((distributionChannel: IDistributionChannel) => ({
                  label: distributionChannel.name,
                  value: distributionChannel,
                })) || ([] as IOptionSelect<IDistributionChannel>[])
            }
          /> */}
          <ChannelSelectSearch
            resetAutoComplete={resetAutoComplete}
            label={'เลือกช่องทางการขาย'}
            onSelect={(e) => {
              const selectedChannelIds = e?.map(
                (channel: any) => channel?.value?.objectId,
              )
              onFilterChange({
                ...filter,
                distributionChannels: selectedChannelIds,
              })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="statusShowCrmTelesale-label">
              ประเภทสถานะคำสั่งซื้อ
            </InputLabel>
            <Select
              key={resetAutoComplete}
              id="statusShowCrmTelesale"
              name="statusShowCrmTelesale"
              labelId="statusShowCrmTelesale-label"
              value={statusShowCrmTelesale}
              onChange={(e) =>
                handleFilterChange({
                  statusShowCrmTelesale: e?.target
                    .value as EStatusTypeOrderCrmTelesale,
                })
              }
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกประเภทสถานะคำสั่งซื้อ</em>
              </MenuItem>
              {menuItemsTypeOrderStatusCRM.map((item, index: number) => (
                <MenuItem value={item.value} key={index}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {/* <Grid item xs={12} lg={6}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="statusOrderShowTracking-label">สถานะ</InputLabel>
            <Select
              id="statusOrderShowTracking"
              name="statusOrderShowTracking"
              labelId="statusOrderShowTracking-label"
              value={statusOrderShowTracking}
              onChange={(e) => handleFilterChange({ statusOrderShowTracking: e?.target.value as EStatusOrderShowTracking })}
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกสถานะ</em>
              </MenuItem>
              {menuItemsTypeStatus.map((item, index: number) => (
                <MenuItem value={item.value} key={index}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid> */}
        <Grid item xs={12} lg={6}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="statusOrderShowTracking-label">
              สถานะการพิมพ์
            </InputLabel>
            <Select
              key={resetAutoComplete}
              id="isPrinted"
              name="isPrinted"
              labelId="isPrinted-label"
              value={isPrinted}
              onChange={(e) =>
                handleFilterChange({ isPrinted: e?.target.value })
              }
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกสถานะ</em>
              </MenuItem>
              <MenuItem value={'all'}>ทั้งหมด</MenuItem>
              <MenuItem value={'printed'}>พิมพ์แล้ว</MenuItem>
              <MenuItem value={'unprinted'}>ยังไม่พิมพ์</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={6}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="statusOrderShowTracking-label">
              สถานะค้างส่ง
            </InputLabel>
            <Select
              key={resetAutoComplete}
              id="overdue"
              name="overdue"
              labelId="overdue-label"
              value={overdue}
              onChange={(e) =>
                handleFilterChange({
                  overdue: e?.target.value as EOrderOverdue,
                })
              }
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกสถานะค้างส่ง</em>
              </MenuItem>
              <MenuItem value={EOrderOverdue.NULL}>ไม่เลือก</MenuItem>
              <MenuItem value={EOrderOverdue.TRUE}>ใช่</MenuItem>
              <MenuItem value={EOrderOverdue.FALSE}>ไม่ใช่</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={12}>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={(startDate, endDate) => {
              handleFilterChange({ startDate, endDate })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="dateType-label">ค้นหาจากวันที่</InputLabel>
            <Select
              key={resetAutoComplete}
              id="dateType"
              name="dateType"
              labelId="dateType-label"
              value={dateType}
              onChange={(e) =>
                handleFilterChange({
                  dateType: e?.target.value as EOrderDateType,
                })
              }
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกรายการค้นหาจากวันที่</em>
              </MenuItem>
              {DATE_TYPES.map((item, index: number) => (
                <MenuItem value={item.value} key={index}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={6}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="paymentMethod-label">รูปแบบการชำระเงิน</InputLabel>
            <Select
              key={resetAutoComplete}
              id="paymentMethod"
              name="paymentMethod"
              labelId="paymentMethod-label"
              value={paymentMethod}
              onChange={(e) =>
                handleFilterChange({
                  paymentMethod: e?.target.value as EOrderType,
                })
              }
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกรายการรูปแบบการชำระเงิน</em>
              </MenuItem>
              {menuItemsTypePaymentMethod.map((item, index: number) => (
                <MenuItem value={item.value} key={index}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={12}>
          <SelectSearch<ICity>
            resetAutoComplete={resetAutoComplete}
            label={'เลือกจังหวัด'}
            search={searchProvince}
            onSearch={setSearchProvince}
            onSelect={(e) => {
              handleFilterChange({ province: e?.value.name_th })
            }}
            options={
              provinceList.map((province: ICity) => ({
                label: province.name_th,
                value: province,
              })) || ([] as IOptionSelect<ICity>[])
            }
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          {/* <SelectSearch<IUserLists>
            resetAutoComplete={resetAutoComplete}
            label={'เลือกคนสร้างคำสั่งซื้อ'}
            onSelect={(e) => handleFilterChange({ actor: e?.value.username })}
            search={searchActor}
            onSearch={handleSearchActor}
            options={
              actorsList.data?.records
                .filter(
                  (userTelesales: IUserLists) => userTelesales.isAvailable,
                )
                .map((userTelesales: IUserLists) => ({
                  label: userTelesales.name,
                  image: userTelesales.image,
                  value: userTelesales,
                })) || ([] as IOptionSelect<IUserLists>[])
            }
          /> */}
          <UserSelectSearch
            resetAutoComplete={resetAutoComplete}
            label={'เลือกคนสร้างคำสั่งซื้อ'}
            onSelect={(e) => handleFilterChange({ actor: e?.value.username })}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          {/* <SelectSearch<IUserLists>
            resetAutoComplete={resetAutoComplete}
            label={'คนรับคอมมิชชั่น'}
            onSelect={(e) =>
              handleFilterChange({ comission: e?.value.username })
            }
            search={searchComission}
            onSearch={handleSearchComission}
            options={
              comissionsList.data?.records
                .filter(
                  (userTelesales: IUserLists) => userTelesales.isAvailable,
                )
                .map((userTelesales: IUserLists) => ({
                  label: userTelesales.name,
                  image: userTelesales.image,
                  value: userTelesales,
                })) || ([] as IOptionSelect<IUserLists>[])
            }
          /> */}
          <UserSelectSearch
            resetAutoComplete={resetAutoComplete}
            label={'คนรับคอมมิชชั่น'}
            onSelect={(e) =>
              handleFilterChange({ comission: e?.value.username })
            }
          />
        </Grid>
        <Grid item xs={6} lg={6}>
          <TextField
            fullWidth
            key={resetAutoComplete}
            id="minTotal"
            name="minTotal"
            label={'ช่วงราคา (น้อยที่สุด)'}
            variant={'filled'}
            type={'number'}
            value={filter.minTotal}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                minTotal:
                  validateMinMaxValue(e.target.value, 0, 999999) || undefined,
              })
            }
          />
        </Grid>
        <Grid item xs={6} lg={6}>
          <TextField
            fullWidth
            id="maxTotal"
            key={resetAutoComplete}
            name="maxTotal"
            label={'ช่วงราคา (มากที่สุด)'}
            variant={'filled'}
            type={'number'}
            value={filter.maxTotal}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                maxTotal:
                  validateMinMaxValue(e.target.value, 1, 999999) || undefined,
              })
            }
          />
        </Grid>
        <Grid item xs={6} lg={6}>
          <TextField
            fullWidth
            id="quantity"
            key={resetAutoComplete}
            name="quantity"
            label={'จำนวนสินค้า'}
            variant={'filled'}
            type={'number'}
            value={filter.quantity}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                quantity:
                  validateMinMaxValue(e.target.value, 0, 1000) || undefined,
              })
            }
          />
        </Grid>
        <Grid item xs={9} lg={9}>
          <Button
            color="primary"
            size="large"
            fullWidth
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => onSearch(filter)}
            sx={{ height: '100%' }}
          >
            ค้นหา
          </Button>
        </Grid>
        <Grid item xs={3} lg={3}>
          <Button
            color="inherit"
            size="large"
            fullWidth
            variant="contained"
            startIcon={!isMobile && <ClearIcon />}
            onClick={() => handleReset()}
            sx={{ height: '100%' }}
          >
            {isMobile ? <ClearIcon /> : 'รีเซ็ต'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  )
}

export default TrackingFilterDrawer
